import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/pages/admin/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { PasswordStrengthMeter, FieldError } from "@/components/FormFeedback";
import { toast } from "sonner";
import {
  validateEmail, normalizeEmail, validateMobile, cleanMobile,
  validatePassword, validateConfirmPassword,
} from "@/lib/validation";
import { cn } from "@/lib/utils";

export default function CreateClinicPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    doctor_name: "",
    clinic_name: "",
    clinic_code: "",
    mobile: "",
    email: "",
    temp_password: "",
    temp_password_confirm: "",
    address: "",
    specialization: "",
    subscription_plan: "TRIAL",
    subscription_start: new Date().toISOString().slice(0, 10),
    subscription_end: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
  });
  const [plans, setPlans] = useState([]);
  const [codeStatus, setCodeStatus] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [codeEdited, setCodeEdited] = useState(false);

  const set = useCallback((k, v) => setForm((f) => ({ ...f, [k]: v })), []);
  const markTouched = (k) => setTouched((t) => ({ ...t, [k]: true }));

  useEffect(() => {
    api.get("/admin/plans").then(({ data }) => setPlans(data)).catch(() => {});
  }, []);

  const currentPlan = plans.find((p) => p.plan_code === form.subscription_plan);

  useEffect(() => {
    if (!form.clinic_name || codeEdited) return;
    api.get("/admin/clinics/suggest-code", { params: { name: form.clinic_name } }).then(({ data }) => {
      set("clinic_code", data.suggestion);
    });
  }, [form.clinic_name, codeEdited, set]);

  useEffect(() => {
    if (!form.clinic_code) return;
    const t = setTimeout(() => {
      api.get("/admin/clinics/check-code", { params: { code: form.clinic_code } }).then(({ data }) => setCodeStatus(data));
    }, 400);
    return () => clearTimeout(t);
  }, [form.clinic_code]);

  useEffect(() => {
    if (validateEmail(form.email)) {
      setEmailStatus(null);
      return;
    }
    const t = setTimeout(() => {
      api.get("/admin/clinics/check-email", { params: { email: normalizeEmail(form.email) } }).then(({ data }) => setEmailStatus(data));
    }, 400);
    return () => clearTimeout(t);
  }, [form.email]);

  const fieldErrors = {
    doctor_name: !form.doctor_name.trim() ? "Doctor name is required" : null,
    clinic_name: !form.clinic_name.trim() ? "Clinic name is required" : null,
    clinic_code: !form.clinic_code.trim() ? "Clinic code is required" : null,
    specialization: !form.specialization.trim() ? "Specialization is required" : null,
    mobile: validateMobile(form.mobile),
    email: validateEmail(form.email) || (emailStatus && !emailStatus.available ? "This email is already registered" : null),
    temp_password: validatePassword(form.temp_password),
    temp_password_confirm: validateConfirmPassword(form.temp_password, form.temp_password_confirm),
  };
  const isValid = Object.values(fieldErrors).every((e) => !e) && (!codeStatus || codeStatus.available);

  const submit = async (e) => {
    e.preventDefault();
    setTouched({ doctor_name: true, clinic_name: true, clinic_code: true, specialization: true, mobile: true, email: true, temp_password: true, temp_password_confirm: true });
    if (!isValid) return;
    setError("");
    setLoading(true);
    try {
      await api.post("/admin/clinics", {
        ...form,
        specialization: form.specialization.trim(),
        email: normalizeEmail(form.email),
        mobile: cleanMobile(form.mobile),
      });
      toast.success("Clinic and Doctor account created successfully");
      navigate("/ali-admin-portal/clinics");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const errClass = (k) => cn(touched[k] && fieldErrors[k] && "border-red-500 focus-visible:ring-red-500");

  return (
    <AdminLayout>
      <h1 className="text-2xl font-semibold text-slate-900" data-testid="create-clinic-heading">
        Create Clinic &amp; Doctor Account
      </h1>
      <form onSubmit={submit} className="mt-6 max-w-3xl card-surface card-surface-hover rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label>Doctor Name</Label>
            <Input data-testid="input-doctor-name" required value={form.doctor_name} onBlur={() => markTouched("doctor_name")} className={errClass("doctor_name")} onChange={(e) => set("doctor_name", e.target.value)} />
            {touched.doctor_name && <FieldError testId="error-doctor-name" message={fieldErrors.doctor_name} />}
          </div>
          <div>
            <Label>Clinic Name</Label>
            <Input data-testid="input-clinic-name" required value={form.clinic_name} onBlur={() => markTouched("clinic_name")} className={errClass("clinic_name")} onChange={(e) => set("clinic_name", e.target.value)} />
            {touched.clinic_name && <FieldError testId="error-clinic-name" message={fieldErrors.clinic_name} />}
          </div>
          <div>
            <Label>Clinic Code (unique, 3-10 chars)</Label>
            <Input
              data-testid="input-clinic-code"
              required
              value={form.clinic_code}
              onChange={(e) => {
                setCodeEdited(true);
                set("clinic_code", e.target.value.toUpperCase());
              }}
            />
            {codeStatus && form.clinic_code && (
              <p data-testid="clinic-code-status" className={`mt-1 text-xs ${codeStatus.available ? "text-green-600" : "text-red-600"}`}>
                {codeStatus.available ? "Code is available" : "Invalid or already taken"}
              </p>
            )}
          </div>
          <div>
            <Label>Mobile</Label>
            <Input
              data-testid="input-mobile"
              required
              value={form.mobile}
              maxLength={10}
              onBlur={() => markTouched("mobile")}
              className={errClass("mobile")}
              onChange={(e) => set("mobile", e.target.value.replace(/[^\d]/g, "").slice(0, 10))}
            />
            {touched.mobile && <FieldError testId="error-mobile" message={fieldErrors.mobile} />}
          </div>
          <div>
            <Label>Email</Label>
            <Input data-testid="input-email" type="email" required value={form.email} onBlur={() => markTouched("email")} className={errClass("email")} onChange={(e) => set("email", e.target.value)} />
            {touched.email && fieldErrors.email ? (
              <FieldError testId="error-email" message={fieldErrors.email} />
            ) : (
              emailStatus && form.email && (
                <p data-testid="email-status" className={`mt-1 text-xs ${emailStatus.available ? "text-green-600" : "text-red-600"}`}>
                  {emailStatus.available ? "Email is available" : "This email is already registered"}
                </p>
              )
            )}
          </div>
          <div>
            <Label>Temporary Password</Label>
            <PasswordInput testId="input-temp-password" required value={form.temp_password} onBlur={() => markTouched("temp_password")} className={errClass("temp_password")} onChange={(e) => set("temp_password", e.target.value)} />
            <PasswordStrengthMeter password={form.temp_password} />
            {touched.temp_password && <FieldError testId="error-temp-password" message={fieldErrors.temp_password} />}
          </div>
          <div>
            <Label>Confirm Temporary Password</Label>
            <PasswordInput testId="input-temp-password-confirm" required value={form.temp_password_confirm} onBlur={() => markTouched("temp_password_confirm")} className={errClass("temp_password_confirm")} onChange={(e) => set("temp_password_confirm", e.target.value)} />
            {touched.temp_password_confirm && <FieldError testId="error-temp-password-confirm" message={fieldErrors.temp_password_confirm} />}
          </div>
          <div>
            <Label>Max Staff (determined by plan)</Label>
            <Input
              data-testid="input-max-staff"
              disabled
              value={currentPlan ? `${currentPlan.max_staff} Active Staff Account${currentPlan.max_staff > 1 ? "s" : ""}` : "..."}
              className="bg-slate-50 text-slate-500"
            />
          </div>
          <div>
            <Label>Doctor Specialization</Label>
            <Input
              data-testid="input-specialization"
              required
              placeholder="e.g. Cardiologist, Pediatrician, ENT Specialist"
              value={form.specialization}
              onBlur={() => markTouched("specialization")}
              className={errClass("specialization")}
              onChange={(e) => set("specialization", e.target.value)}
            />
            {touched.specialization && <FieldError testId="error-specialization" message={fieldErrors.specialization} />}
          </div>
          <div className="sm:col-span-2">
            <Label>Clinic Address</Label>
            <Input data-testid="input-address" value={form.address} onChange={(e) => set("address", e.target.value)} />
          </div>
          <div>
            <Label>Subscription Plan</Label>
            <Select value={form.subscription_plan} onValueChange={(v) => set("subscription_plan", v)}>
              <SelectTrigger data-testid="select-plan">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {plans.filter((p) => p.is_active).map((p) => (
                  <SelectItem key={p.plan_code} value={p.plan_code}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Start Date</Label>
            <DatePicker testId="input-start-date" required value={form.subscription_start} onChange={(v) => set("subscription_start", v)} />
          </div>
          <div>
            <Label>Expiry Date</Label>
            <DatePicker testId="input-expiry-date" required value={form.subscription_end} onChange={(v) => set("subscription_end", v)} />
          </div>
        </div>
        {error && (
          <p data-testid="create-clinic-error" className="mt-4 text-sm text-red-600">
            {error}
          </p>
        )}
        <Button data-testid="create-clinic-submit-btn" type="submit" disabled={loading || !isValid} className="mt-6 bg-teal-700 hover:bg-teal-800">
          {loading ? "Creating..." : "Create Clinic"}
        </Button>
      </form>
    </AdminLayout>
  );
}
